import i18next, { type Resource } from "i18next";
import arCommon from "@/shared/config/locales/ar/common.json" with {
	type: "json",
};
import enCommon from "@/shared/config/locales/en/common.json" with {
	type: "json",
};
import { ApplicationError } from "@/shared/lib/errors";
import {
	INSTALLED_LANGUAGE_LABELS,
	INSTALLED_LANGUAGES,
	isInstalledLanguage,
} from "./i18n/installed-languages";
import type {
	I18nInterface,
	Locale,
	TranslationOptions,
	TranslationVars,
} from "./i18n/types";
import {
	StorageKeys,
	StorageSchemas,
	typedLocalStorage,
} from "./storage/typed-storage";

// Dynamic language loading state
let initialized = false;
// Language labels cache - starts with installed language labels, can be enriched dynamically.
// Mutated in place by `addLanguageResource` and `loadLanguageLabels`; consumers
// always receive a shallow copy via `getLanguageLabels()`.
const languageLabelsCache: Record<string, string> = {
	...INSTALLED_LANGUAGE_LABELS,
};

const STATIC_RESOURCES = {
	en: { common: enCommon },
	ar: { common: arCommon },
} satisfies Resource;

/**
 * Get installed languages - these are the languages that have translation files.
 * This is a fixed list determined at build time.
 */
export function getInstalledLanguages(): readonly string[] {
	return INSTALLED_LANGUAGES;
}

/**
 * Resolve language labels for installed languages from the bundled catalogs.
 *
 * Installed languages ship in `STATIC_RESOURCES`, so their labels are always
 * derivable from the in-memory bundle — no network access is involved. Falls
 * back to the upper-cased language code so the UI never shows a missing label.
 */
function loadLanguageLabels(): Record<string, string> {
	const i18n = ensureInitialized();

	for (const lang of INSTALLED_LANGUAGES) {
		if (languageLabelsCache[lang] || !i18n.hasResourceBundle(lang, "common")) {
			continue;
		}
		const data = i18n.getResourceBundle(lang, "common") as Record<
			string,
			unknown
		>;
		const labelKey = lang === "en" ? "language_english" : `language_${lang}`;
		const label = data?.[labelKey];
		languageLabelsCache[lang] =
			typeof label === "string" && label ? label : lang.toUpperCase();
	}

	return { ...languageLabelsCache };
}

/**
 * Make an installed language's resources active in i18next.
 *
 * Installed languages ship in `STATIC_RESOURCES` at build time, so this
 * resolves entirely from the bundle. It used to `fetch('/locales/…')` and
 * `addResourceBundle(…, overwrite: true)` over the bundled copy — that second
 * copy under `web/public/locales/` silently diverged from the bundled one and
 * has been removed; see the note in `ensureInitialized`.
 *
 * Still throws for non-installed languages: adding a language requires
 * shipping its catalog and extending `INSTALLED_LANGUAGES`, which is a
 * build-time change.
 */
export function addLanguageResource(languageCode: string): Promise<void> {
	if (typeof window === "undefined") {
		return Promise.resolve();
	}

	if (!isInstalledLanguage(languageCode)) {
		return Promise.reject(
			ApplicationError.badRequest(
				`Language ${languageCode} is not installed`,
				"LANGUAGE_NOT_INSTALLED",
				{ languageCode },
			),
		);
	}

	const i18n = ensureInitialized();
	const translations = (i18n.getResourceBundle(languageCode, "common") ??
		{}) as Record<string, unknown>;

	const labelKey =
		languageCode === "en" ? "language_english" : `language_${languageCode}`;
	const rawLabel = translations[labelKey];
	const label =
		typeof rawLabel === "string" && rawLabel
			? rawLabel
			: languageCode.toUpperCase();
	languageLabelsCache[languageCode] = label;

	window.dispatchEvent(
		new CustomEvent("language-added", { detail: { languageCode, label } }),
	);

	return Promise.resolve();
}

/**
 * Refresh installed languages — resolves labels for all installed languages
 * from the bundled catalogs. Kept async-shaped for callers that `await` it.
 */
export function refreshAvailableLanguages(): Promise<string[]> {
	loadLanguageLabels();
	return Promise.resolve([...INSTALLED_LANGUAGES]);
}

export function getLanguageLabels(): Record<string, string> {
	return { ...languageLabelsCache };
}

// Initialize i18next with dynamic loading
function ensureInitialized(): typeof i18next {
	if (initialized) {
		return i18next;
	}

	// Initialize i18next with statically-imported resources only.
	// `i18next-http-backend` was previously wired via `.use(Backend)` but
	// `STATIC_RESOURCES` (above) ships the AR/EN JSON files at build time,
	// so the backend never had a code path that could reach `loadPath`.
	// Removing it sheds a 17 kB dependency.
	i18next.init({
		resources: STATIC_RESOURCES,
		lng: "en",
		fallbackLng: "en",
		defaultNS: "common",
		ns: ["common"],
		interpolation: { escapeValue: false },
		returnObjects: false,
	});

	initialized = true;

	// NOTE: installed languages are NOT re-fetched here. They already ship in
	// `STATIC_RESOURCES`, so the old startup loop re-downloaded ~1 MB of JSON
	// only to `addResourceBundle(..., overwrite: true)` it back over the
	// bundled copy. That made `public/locales/` a second, silently-diverging
	// source of truth: strings rendered from the bundle on first paint, then
	// visibly flipped to whatever `public/` happened to contain. The public
	// copy has been removed; the bundled catalogs are the only source.

	return i18next;
}

const HUMANIZE_PREFIX_RE =
	/^(kpi_|msg_|chart_|dashboard_|response_time_|operation_|segment_|funnel_|day_|slot_)/;
function humanize(raw: string): string {
	const stripped = raw
		.replace(HUMANIZE_PREFIX_RE, "")
		.replace(/_/g, " ")
		.trim();
	return stripped
		.split(" ")
		.filter(Boolean)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");
}

function pickLocale(): string {
	if (typeof window !== "undefined") {
		try {
			const loc = typedLocalStorage.getItem(
				StorageKeys.locale,
				StorageSchemas.locale,
			);
			if (loc) {
				return loc;
			}
		} catch {
			// Locale detection errors are non-critical - fallback to default
		}
	}
	return "en";
}

/**
 * The locale every message on screen is resolved against — the persisted user
 * choice, the same source `i18n.t` / `getMessage` use. Exported for the few
 * non-React consumers that must localize outside the component tree (the Zod
 * global error map in `zod-locale.ts`); components should read
 * `useLanguage()` so they re-render on a change.
 */
export function getLocale(): string {
	return pickLocale();
}

function translate(
	key: string,
	isLocalizedOrLocale?: boolean | string,
	options?: TranslationVars,
): string {
	let lng: string;
	if (typeof isLocalizedOrLocale === "string") {
		// New: accept locale string directly
		lng = isLocalizedOrLocale;
	} else if (isLocalizedOrLocale === undefined) {
		lng = pickLocale();
	} else {
		// Legacy: boolean isLocalized
		lng = isLocalizedOrLocale ? "ar" : "en";
	}
	const i18n = ensureInitialized();
	const translateOptions: TranslationOptions = { lng, ...options };
	const value = i18n.t(key, translateOptions);
	if (value === key) {
		return humanize(key);
	}
	return value;
}

export function getMessageWithVars(
	key: string,
	isLocalizedOrLocale: boolean | string | undefined,
	vars: TranslationVars,
): string {
	return translate(key, isLocalizedOrLocale, vars);
}

export const messages = {
	validation: {
		required: (field: string) =>
			translate("validation.required", undefined, { field }),
		thisFieldIsRequired: () => translate("validation.thisFieldIsRequired"),
		invalidFormat: () => translate("validation.invalidFormat"),
		invalidDate: () => translate("validation.invalidDate"),
		invalidTime: () => translate("validation.invalidTime"),
		invalidPhone: () => translate("validation.invalidPhone"),
		phoneFormatNotRecognized: () =>
			translate("validation.phoneFormatNotRecognized"),
		phoneHasInvalidCountryCode: () =>
			translate("validation.phoneHasInvalidCountryCode"),
		phoneContainsInvalidCharacters: () =>
			translate("validation.phoneContainsInvalidCharacters"),
		phoneIsTooShort: () => translate("phoneIsTooShort"),
		phoneIsTooLong: () => translate("phoneIsTooLong"),
		phoneHasInvalidLengthForCountry: () =>
			translate("validation.phoneHasInvalidLengthForCountry"),
		phoneInvalidFormat: () => translate("validation.phoneInvalidFormat"),
		phoneMayHaveInvalidAreaCode: () =>
			translate("validation.phoneMayHaveInvalidAreaCode"),
		phoneFormatIsInvalid: () => translate("validation.phoneFormatIsInvalid"),
		invalidName: () => translate("validation.invalidName"),
		nameRequired: () => translate("validation.nameRequired"),
		nameTooShort: () => translate("validation.nameTooShort"),
		nameInvalidCharacters: () => translate("validation.nameInvalidCharacters"),
		nameWordsTooShort: () => translate("validation.nameWordsTooShort"),
		nameTooLong: () => translate("validation.nameTooLong"),
	},
	// Relative time formatting helpers
	time: {
		justNow: () => translate("time.justNow"),
		minAgo: (n: number) => translate("time.minAgo", undefined, { n }),
		hoursAgo: (n: number) => translate("time.hoursAgo", undefined, { n }),
		daysAgo: (n: number) => translate("time.daysAgo", undefined, { n }),
	},
	grid: {
		none: () => translate("grid.none"),
	},
} as const;

// Export installed languages as the source of truth
// Components should use getInstalledLanguages() for the list of all installed languages
// and getLanguageLabels() for their display names
const _AVAILABLE_LANGUAGES = INSTALLED_LANGUAGES;
const _LANGUAGE_LABELS: Readonly<Record<string, string>> = languageLabelsCache;

export type { Direction } from "./i18n/direction";
export { getDirection } from "./i18n/direction";
export {
	formatCompactNumber,
	formatNumber,
	formatPercent,
} from "./i18n/format-number";
export type { InstalledLanguage } from "./i18n/installed-languages";
// Re-export installed languages utilities
export {
	filterToInstalledLanguages,
	INSTALLED_LANGUAGE_LABELS,
	INSTALLED_LANGUAGES,
	isInstalledLanguage,
	normalizeAvailableLanguages,
} from "./i18n/installed-languages";
export { useDirection } from "./i18n/use-direction";
// NOTE: useFormatNumber is intentionally NOT re-exported here — it depends on
// the language store, which itself depends on this module (i18n.changeLanguage),
// so re-exporting it would create an import cycle. Its only consumer imports it
// directly from "./i18n/use-format-number".

export const i18n: I18nInterface<typeof messages> = {
	// Resolve the locale from the same source as `getMessage`/`translate`
	// (`pickLocale()` → persisted locale), rather than relying on i18next's
	// global `.language`. The global language can lag behind the user's
	// selected locale at render time (it is set via an async `changeLanguage`
	// and `i18n.t` callers do not subscribe to language changes), which made
	// `i18n.t(...)` fall back to English while `getMessage(...)` rendered the
	// correct locale on the same screen.
	t: (key: string, fallback?: string) =>
		ensureInitialized().t(key, { lng: pickLocale(), defaultValue: fallback }),
	getMessage: (key: string, isLocalizedOrLocale?: boolean | string): string =>
		translate(key, isLocalizedOrLocale),
	getUnknownUserLabel: (isLocalizedOrLocale?: boolean | string): string =>
		translate("phone_unknown_label", isLocalizedOrLocale),
	changeLanguage: (lng: Locale) => ensureInitialized().changeLanguage(lng),
	messages,
};
